import { useMemo } from "react";
import DSBaseTable, {
  Column,
} from "views/system-design/Table/DSBaseTable/components/DSBaseTable";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSCollapsibleText from "views/system-design/DSCollapsibleText";
import DSAlert from "views/system-design/DSAlert";
import { useGroupedClockErrors } from "views/Monitor/hooks/useGroupedClockErrors";
import {
  useIgnoredClockErrors,
  useCreateIgnoredClockError,
} from "views/Monitor/hooks/useIgnoredClockErrors";
import firebaseService from "firebase_service/firebaseService";
import { IgnoredClockError } from "api/admin";

// One failing error attributed to a single clock. The grouped-errors endpoint
// returns an error once with every clock that hit it; the two ignore actions
// are per-clock ("only this clock") and per-version ("all clocks"), so the
// table is flattened to one row per (error, clock).
type ClockErrorRow = {
  id: string;
  log: string;
  errorMessage: string;
  method: string;
  class: string;
  clockId: string;
  count: number;
  lastOccurrence: string;
};

const columns: Column<ClockErrorRow>[] = [
  { field: "log", label: "Error" },
  { field: "method", label: "Method" },
  { field: "class", label: "Class" },
  { field: "clockId", label: "Clock" },
  { field: "count", label: "Count" },
  { field: "id", label: "Actions" },
];

// Mirror the backend's ignore match (errorMessage/method/class, plus clockId
// when the rule is clock-scoped). The backend uses SQL LIKE; rules created from
// this table carry the exact strings, so exact equality hides exactly the rows
// that were just ignored.
const isIgnored = (row: ClockErrorRow, ignored: IgnoredClockError[]) =>
  ignored.some(
    (ie) =>
      ie.deletedAt == null &&
      ie.errorMessage === row.errorMessage &&
      ie.method === row.method &&
      ie.class === row.class &&
      (ie.clockId == null || ie.clockId === row.clockId),
  );

const ClocksErrorsTable = () => {
  const publicVersion = firebaseService.general?.public ?? null;

  const { data: grouped, isLoading } = useGroupedClockErrors();
  const { data: ignored } = useIgnoredClockErrors(publicVersion);
  const {
    mutate: createIgnoredError,
    variables: pendingIgnore,
    isPending: isIgnoring,
    error: ignoreError,
    reset: resetIgnoreError,
  } = useCreateIgnoredClockError();

  const rows = useMemo<ClockErrorRow[]>(() => {
    const ignoredList = ignored ?? [];
    return (
      (grouped ?? [])
        // Soft-deleted errors are excluded from the cron's failure check, so they
        // did not cause the failure either.
        .filter((group) => !group.isDeleted)
        .flatMap((group) =>
          group.clocks.map((clock) => ({
            id: `${group.errorMessage}|${group.method}|${group.class}|${clock.clockId}`,
            log: group.log,
            errorMessage: group.errorMessage,
            method: group.method,
            class: group.class,
            clockId: clock.clockId,
            count: clock.count,
            lastOccurrence: group.lastOccurrence,
          })),
        )
        .filter((row) => !isIgnored(row, ignoredList))
    );
  }, [grouped, ignored]);

  const handleIgnoreVersion = (row: ClockErrorRow) => {
    if (!publicVersion) return;
    createIgnoredError({
      errorMessage: row.errorMessage,
      method: row.method,
      class: row.class,
      version: publicVersion,
      clockId: null,
    });
  };

  const handleIgnoreClock = (row: ClockErrorRow) => {
    if (!publicVersion) return;
    createIgnoredError({
      errorMessage: row.errorMessage,
      method: row.method,
      class: row.class,
      version: publicVersion,
      clockId: row.clockId,
    });
  };

  // The two buttons on a row differ only by clockId, so match on it too.
  const isPendingFor = (row: ClockErrorRow, clockId: string | null) =>
    isIgnoring &&
    pendingIgnore?.errorMessage === row.errorMessage &&
    pendingIgnore?.method === row.method &&
    pendingIgnore?.class === row.class &&
    (pendingIgnore?.clockId ?? null) === clockId;

  const rowBuilder = (row: ClockErrorRow) => (
    <>
      <DSTableCell sx={{ minWidth: 320, maxWidth: 560 }}>
        <DSCollapsibleText text={row.log} maxLines={2} />
      </DSTableCell>
      <DSTableCell>{row.method || "-"}</DSTableCell>
      <DSTableCell>{row.class || "-"}</DSTableCell>
      <DSTableCell>{row.clockId}</DSTableCell>
      <DSTableCell>{row.count}</DSTableCell>
      <DSTableCell sx={{ whiteSpace: "nowrap" }}>
        <DSBox sx={{ display: "flex", gap: 0.5 }}>
          <DSButton
            size="small"
            variant="outlined"
            color="warning"
            disabled={!publicVersion || isPendingFor(row, null)}
            onClick={() => handleIgnoreVersion(row)}
            title="Ignore this error for every clock on this version"
          >
            {isPendingFor(row, null) ? "…" : "Ignore (version)"}
          </DSButton>
          <DSButton
            size="small"
            variant="outlined"
            color="warning"
            disabled={!publicVersion || isPendingFor(row, row.clockId)}
            onClick={() => handleIgnoreClock(row)}
            title="Ignore this error only for this clock"
          >
            {isPendingFor(row, row.clockId) ? "…" : "Ignore (clock)"}
          </DSButton>
        </DSBox>
      </DSTableCell>
    </>
  );

  return (
    <DSBox>
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <DSTypography variant="caption" color="error">
          Failing errors{publicVersion ? ` (version ≥ ${publicVersion})` : ""}
        </DSTypography>
        {!isLoading && (
          <DSTypography variant="caption" color="text.secondary">
            {rows.length} error{rows.length === 1 ? "" : "s"}
          </DSTypography>
        )}
      </DSBox>

      {ignoreError && (
        <DSAlert severity="error" onClose={resetIgnoreError} sx={{ mb: 1 }}>
          Failed to ignore error: {(ignoreError as Error).message}
        </DSAlert>
      )}

      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <DSCircularProgress size={24} />
        </DSBox>
      ) : rows.length === 0 ? (
        <DSTypography variant="body2" color="success.main">
          ✓ No failing errors — all clock errors are ignored or resolved.
        </DSTypography>
      ) : (
        <DSBox sx={{ maxHeight: 420 }}>
          <DSBaseTable
            data={rows}
            columns={columns}
            rowBuilder={rowBuilder}
            getRowId={(row) => row.id}
            defaultRowsPerPage={50}
          />
        </DSBox>
      )}
    </DSBox>
  );
};

export default ClocksErrorsTable;
